import { useState, useEffect, createContext, useContext } from 'react';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Get a safe API base URL - handles undefined, "undefined" string, and empty values
const getApiBaseUrl = () => {
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  // Return empty string (relative URL) if env var is missing, undefined, or "undefined" string
  if (!envUrl || envUrl === 'undefined' || envUrl === 'null') {
    return '';
  }
  // Remove trailing slash if present and ensure clean URL
  return envUrl.trim().replace(/\/$/, '');
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem('adminToken'));
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  const API = getApiBaseUrl();

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API}/api/admin/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.ok) {
          setIsAuthenticated(true);
        } else {
          logout();
        }
      } catch (error) {
        logout();
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, [token, API]);

  const login = async (username, password) => {
    try {
      // Build the login URL - use relative path if API is empty
      const loginUrl = API ? `${API}/api/admin/login` : '/api/admin/login';
      
      // Validate username and password are not empty
      if (!username || !password) {
        throw new Error('Username and password are required');
      }
      
      const response = await fetch(loginUrl, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ 
          username: username.trim(), 
          password: password 
        })
      });

      if (!response.ok) {
        let errorMessage = 'Login failed';
        try {
          const error = await response.json();
          errorMessage = error.detail || errorMessage;
        } catch (e) {
          // Response wasn't JSON
          errorMessage = `Login failed with status ${response.status}`;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      const accessToken = data.access_token || data.token;
      
      if (!accessToken) {
        throw new Error('No access token received from server');
      }
      
      localStorage.setItem('adminToken', accessToken);
      setToken(accessToken);
      setIsAuthenticated(true);
      return data;
    } catch (error) {
      console.error('Login error:', error);
      
      // Provide more helpful error messages for network issues
      if (error.name === 'TypeError') {
        // Safari-specific error
        if (error.message.includes('pattern') || error.message === 'Load failed') {
          throw new Error('Connection error: Please check your internet connection and try again.');
        }
        // General fetch failure
        if (error.message === 'Failed to fetch') {
          throw new Error('Unable to connect to server. Please try again.');
        }
      }
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('adminToken');
    setToken(null);
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider value={{ token, isAuthenticated, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
